import { useEffect } from 'react';
import { reaction } from 'mobx';
import { usePlatform, ScreenOrientation } from '~/services/platform';
import { useStore } from '~/stores';
import { otaLog } from '~/services/ota/OtaDebugLogger';

/**
 * Sincroniza a orientação FÍSICA do device → servidor, em QUALQUER tela.
 *
 * Montado UMA vez no App (sempre ativo): cobre "Aguardando aprovação" (cena
 * Auth), "Aguardando conteúdo" (NoContent) e o Stream tocando.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * Causa-raiz (2026-06-06, diagnóstico do Renato + investigação):
 *
 * 1ª versão (v1.0.150) reconciliava reagindo ao `deviceId` — que é setado
 * ANTES do registro. O PATCH /api/devices/{id}/orientation caía em "Device
 * not registered" (player row ainda não existe) e era perdido. O registro
 * então gravava a orientação do momento (tablet na mão / viewport não
 * assentado = landscape). Como `deviceId` não mudava mais, o reconcile nunca
 * re-disparava; e se o tablet bootou já em portrait, NENHUM evento de
 * rotação dispara → servidor preso em landscape pra sempre.
 *
 * Fix (v1.0.151):
 *   • Reconcilia reagindo a `registrationStatus`/`isAuthenticated` — dispara
 *     DEPOIS do registro, quando o player JÁ EXISTE no servidor (o PATCH
 *     pega). Cobre fresh install, re-launch e aprovação.
 *   • Empurra sempre a orientação FÍSICA (`getOrientation()`) e LIMPA o
 *     override — "rotação física vence". Cura inclusive overrides grudados
 *     por PATCHes antigos que caíram em not_registered mas mesmo assim
 *     pinaram `selectedOrientation` no localStorage.
 *   • `onOrientationChange` cobre rotações em runtime (já passa pelo poll de
 *     7s do AndroidPlatformService, que pega rotação/montagem tardia).
 *
 * Gate: só roda onde a orientação é ajustável (`supportsOrientationChange`
 * = Android/Browser). TV (Tizen/webOS) é landscape-fixo → no-op.
 */
export const useOrientationSync = (): void => {
  const platform = usePlatform();
  const { authStore, syncStore } = useStore();

  useEffect(() => {
    if (!platform.supportsOrientationChange()) return;

    // Orientação já push'ada com sucesso nesta sessão.
    let lastSyncedPhysicalOrientation: ScreenOrientation | null = null;

    /**
     * Teto de pushes de orientação por janela (backstop anti-loop, 2026-10-26).
     * Vide o GUARD em `pushPhysical`. Seis cobre com folga o uso real (boot +
     * algumas rotações de verdade) e corta uma rajada de 87/min em 6.
     */
    const PUSH_WINDOW_MS = 60_000;
    const MAX_PUSHES_PER_WINDOW = 6;
    let windowStartedAtMs = 0;
    let pushesInWindow = 0;

    const ROTATION_REASONS = new Set(['rotação física', 'watchdog-rotation']);

    const shouldSkipBootResync = (reason: string): boolean => {
      if (reason !== 'watchdog-first' && reason !== 'registrado') {
        return false;
      }
      if (syncStore.status !== 'success' || !syncStore.lastSyncTime) {
        return false;
      }
      const ageMs = Date.parse(syncStore.lastSyncTime);
      if (!Number.isFinite(ageMs)) return false;
      return Date.now() - ageMs < 90_000;
    };

    const pushPhysical = (reason: string): Promise<boolean> => {
      // GUARD (2026-07-08, "admin sempre prevalece"): quando o player tem um
      // tamanho/orientação CUSTOM setado no admin, NÃO empurrar a orientação
      // física — o PATCH { orientation } (sem orientacao_id) resetaria o
      // orientacao_id custom no server e o tamanho reverteria pro default
      // (tela cheia). Tablet SEM custom continua empurrando → rotação normal.
      // O server (DeviceRegistrationService) também protege, mas suprimir aqui
      // mata o PATCH inútil de 5 em 5s + o forceResync que puxava o revert.
      if (syncStore.hasCustomOrientation) {
        otaLog.info(
          'orientation push suprimido — hasCustomOrientation (admin prevalece)',
          {
            reason,
          },
        );
        return Promise.resolve(true);
      }
      const physical = platform.getOrientation();

      // GUARD anti-loop (2026-10-26, pós-incidente de produção). Todo push de
      // orientação arrasta um forceResync, e o sync é a chamada mais cara do
      // servidor. Um player browser em loop fez 87 PATCH /orientation + 148
      // POST /sync/media-library num ÚNICO minuto: o pool de 60 conexões saturou
      // em 30s e a produção ficou 30 minutos fora do ar. Dois freios, nesta ordem:
      //
      // (1) DEDUP — o servidor já está nesta orientação nesta sessão. Não há o que
      //     propagar nem o que re-sincronizar. Cobre o caso comum de rajada: a
      //     reaction de auth re-disparando, o watchdog e o evento de rotação
      //     empurrando todos o MESMO valor.
      if (lastSyncedPhysicalOrientation === physical) {
        otaLog.info('orientation push ignorado — servidor já nesta orientação', {
          reason,
          physical,
        });
        return Promise.resolve(true);
      }

      // (2) TETO POR JANELA — backstop pro caso de a orientação oscilar de verdade
      //     (janela de browser redimensionada em cima da diagonal, viewport
      //     instável em kiosk), onde o dedup não segura porque o valor muda mesmo.
      //     Teto, e não intervalo mínimo: um intervalo mínimo atrasaria a primeira
      //     rotação depois do boot (o push de reconcile já teria "gasto" a janela),
      //     o que é regressão de responsividade sem ganho. Com teto, o uso real
      //     passa inteiro e só a rajada é cortada.
      //     Retorna false de propósito: é o sinal que faz o watchdog (poll de 5s)
      //     limpar `lastPushedOrientation` e re-tentar quando a janela virar.
      const nowMs = Date.now();
      if (nowMs - windowStartedAtMs >= PUSH_WINDOW_MS) {
        windowStartedAtMs = nowMs;
        pushesInWindow = 0;
      }
      if (pushesInWindow >= MAX_PUSHES_PER_WINDOW) {
        otaLog.warn('orientation push barrado — teto da janela (anti-loop)', {
          reason,
          physical,
          pushesInWindow,
        });
        return Promise.resolve(false);
      }
      pushesInWindow += 1;

      const orientationChanged =
        lastSyncedPhysicalOrientation !== null &&
        lastSyncedPhysicalOrientation !== physical;
      const isRotation = ROTATION_REASONS.has(reason);

      console.log(
        `[useOrientationSync] 📱 ${reason} → orientação física ao servidor:`,
        physical,
      );
      authStore.setSelectedOrientation(null);
      return authStore.updateOrientation(physical).then((ok) => {
        if (!ok) {
          otaLog.error('orientation push FAILED', { reason, physical });
          return false;
        }
        otaLog.info('orientation push OK', { reason, physical });
        lastSyncedPhysicalOrientation = physical;

        if (isRotation || orientationChanged) {
          return syncStore
            .forceResync({ resetPlaylistFromStart: true })
            .then(() => true);
        }

        if (shouldSkipBootResync(reason)) {
          otaLog.info(
            'orientation push: skip forceResync (boot reconcile, sync recente)',
            {
              reason,
              lastSync: syncStore.lastSyncTime,
            },
          );
          return true;
        }

        return syncStore
          .forceResync({ resetPlaylistFromStart: true })
          .then(() => true);
      });
    };

    // Reconcile: assim que o device estiver REGISTRADO no servidor (player
    // row existe), alinha o servidor à orientação física real. fireImmediately
    // cobre device já registrado (re-launch); a reaction cobre o registro
    // concluir depois (fresh install). updateOrientation é no-op sem deviceId.
    const disposeReconcile = reaction(
      () => ({
        deviceId: authStore.deviceId,
        status: authStore.registrationStatus,
        authenticated: authStore.isAuthenticated,
      }),
      ({ deviceId, status, authenticated }) => {
        const registered =
          authenticated ||
          status === 'pending_approval' ||
          status === 'approved';
        if (deviceId && registered) {
          pushPhysical('registrado');
        }
      },
      { fireImmediately: true },
    );

    // Rotação física em runtime vence: grava no servidor + re-sync.
    const unsubscribe = platform.onOrientationChange(() => {
      pushPhysical('rotação física');
    });

    // v1.0.190 (Renato 2026-06-14): WATCHDOG independente do callback.
    //
    // Em kiosk ON com tablet rotacionado fisicamente, confirmado em campo
    // que o callback `onOrientationChange` NÃO disparava (server preso na
    // orientação antiga mesmo após o app visualmente acompanhar a rotação).
    //
    // Causa provável: o `evaluateJavascript` que dispara `resize` event no
    // WebView em `MainActivity.onConfigurationChanged` chega antes do WebView
    // atualizar `window.inner*` em kiosk ON (immersive mode adiciona delay),
    // fazendo `detectOrientation` retornar o estado antigo → dedup no
    // `handleOrientationChange` impede a notificação.
    //
    // Watchdog poll a cada 5s: lê `platform.getOrientation()` (que reflete
    // o `window.innerWidth/Height` ATUAL) e compara com `lastSyncedOrientation`.
    // Se mudou e ainda não sincronizou, dispara `pushPhysical` manualmente.
    //
    // Custo: 1 leitura por 5s — desprezível. Idempotente (dedup interno do
    // `lastSyncedOrientation`).
    // v1.0.190 (Renato 2026-06-15): watchdog reformulado.
    //
    // Bug descoberto via logs: a `reaction` acima dispara o handler com
    // `fireImmediately: true` no mount inicial — mas naquele momento
    // `authStore` ainda está em estado idle (deviceId vazio ou sem
    // registrationStatus approved). Quando o auth termina depois (boot
    // assíncrono), a reaction NÃO re-dispara — observado em campo no Tab A7
    // após reboot do device: tablet portrait físico, server preso em landscape
    // (último estado registrado), `pushPhysical('registrado')` nunca rodou.
    //
    // Fix: watchdog próprio que dispara push quando:
    // (a) Device está REGISTRADO (auth completou)
    // (b) E a orientação física atual ainda NÃO foi push'ada com sucesso
    //
    // Cobre AMBOS cenários:
    // - Mudança de orientação física (current != lastPushed)
    // - Auth tardio (current foi setado no init mas lastPushed=null até ter auth)
    let lastPushedOrientation: ScreenOrientation | null = null;
    // v1.0.199 (Renato 2026-06-23): exige orientação estável em 2 polls
    // consecutivos (10s) antes de forceResync. WebView kiosk flapping
    // portrait↔landscape gerava sync storm + reload de vídeo mid-playback.
    let pendingWatchdogOrientation: ScreenOrientation | null = null;
    let watchdogStableTicks = 0;
    const WATCHDOG_STABLE_TICKS_REQUIRED = 1;
    // v1.0.190 (Renato 2026-06-15): watchdog removido gate `registered`.
    // Logs em campo revelaram: authStore fica permanently `status=idle,
    // authenticated=false` em cenários offline-cache/recovery, mesmo com
    // o app tocando conteúdo normal. O endpoint PATCH /api/devices/{id}/orientation
    // aceita qualquer device com deviceId válido (testado via curl). Gate só
    // por deviceId presente garante que orientação física é sempre propagada.
    const watchdog = window.setInterval(() => {
      const current = platform.getOrientation();
      const id = authStore.deviceId;
      if (!id) return;

      if (current === lastPushedOrientation) {
        pendingWatchdogOrientation = null;
        watchdogStableTicks = 0;
        return;
      }

      const isFirst = lastPushedOrientation === null;
      if (isFirst) {
        pendingWatchdogOrientation = null;
        watchdogStableTicks = 0;
        lastPushedOrientation = current;
        void pushPhysical('watchdog-first').then((ok) => {
          if (!ok) lastPushedOrientation = null;
        });
        return;
      }

      if (current === pendingWatchdogOrientation) {
        watchdogStableTicks += 1;
      } else {
        pendingWatchdogOrientation = current;
        watchdogStableTicks = 1;
      }

      if (watchdogStableTicks >= WATCHDOG_STABLE_TICKS_REQUIRED) {
        const target = current;
        pendingWatchdogOrientation = null;
        watchdogStableTicks = 0;
        lastPushedOrientation = target;
        void pushPhysical('watchdog-rotation').then((ok) => {
          if (!ok) lastPushedOrientation = null;
        });
      }
    }, 5000);

    return () => {
      disposeReconcile();
      unsubscribe();
      window.clearInterval(watchdog);
    };
  }, [platform, authStore, syncStore]);
};

export default useOrientationSync;
